/*
 * The one button. A variant says what the action is, never how it looks, so a "danger" button is
 * red in both themes without a caller ever naming a colour. The label is a string the caller has
 * already translated — no component in this tree contains German.
 *
 * The six variants are ranks, not colours: `primary` is the one action of a screen or a dialog,
 * `secondary` the accent outline of the second-rank action (*Browse channels*, *Add people*),
 * `neutral` the grey outline (*Cancel*, *Download*), `ghost` an action in a row, a bar or a header,
 * `danger` the filled confirm inside a destructive dialog and `danger-quiet` the red ink of a
 * destructive action that stands on a page (*Leave*, *Sign out everywhere*). `quiet` composes with
 * any of them for a glyph that rests muted in a dense row and comes up under the pointer; `icon`
 * makes a glyph-only square; `busy` puts a spinner in the leading slot and leaves the label where
 * it is, so a submit in flight never changes width.
 *
 * `href` decides the element: a control that *goes somewhere* is an `<a>`, so the browser's own
 * gestures still work (middle click, "open in a new tab", a download), and everything else is a
 * `<button>`. Both wear the same variant, which is what stops peer actions — *Download* beside
 * *Preview*, *Message* on a profile — from reading as two different kinds of thing.
 */

import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode, Ref } from "react"
import { cx } from "../lib/cx.ts"
import "./ui.css"

export type ButtonVariant =
	"primary" | "secondary" | "neutral" | "ghost" | "danger" | "danger-quiet"

export type ButtonSize = "sm" | "md" | "lg"

interface Shared {
	variant?: ButtonVariant
	/** The rung of the control ladder: 28, 32 or 40 px. `md` is the button on a page. */
	size?: ButtonSize
	/** Muted at rest, full colour under the pointer — the glyph of a dense row. */
	quiet?: boolean
	/** A glyph and nothing else: the button is a square of its size. */
	icon?: boolean
	/** The action is in flight: a spinner takes the leading slot and the label stays put. */
	busy?: boolean
	/** Visible label; leave it out only when `aria-label` names the button instead. */
	label?: string
	/** An icon or a badge rendered before the label. */
	before?: ReactNode
	/** An icon rendered after the label — the trailing chevron of *Next ›*. */
	after?: ReactNode
	className?: string
}

export interface PressProps
	extends Shared, Omit<ButtonHTMLAttributes<HTMLButtonElement>, "className"> {
	/** Absent: the control acts here and now, and is a `<button>`. */
	href?: undefined
	ref?: Ref<HTMLButtonElement>
}

export interface LinkProps
	extends Shared, Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "className"> {
	/** Present: the control navigates, and is an `<a>`. */
	href: string
	ref?: Ref<HTMLAnchorElement>
}

export type ButtonProps = PressProps | LinkProps

/** The class list every button wears, from the props that decide its look. */
function classesOf({ variant = "ghost", size = "md", quiet, icon, className }: Shared): string {
	return cx(
		"tb-button",
		`tb-button--${variant}`,
		size !== "md" && `tb-button--${size}`,
		quiet === true && "tb-button--quiet",
		icon === true && "tb-button--icon",
		className,
	)
}

/** The leading slot, the label and the trailing slot; the spinner takes the leading slot while busy. */
function Content({
	busy,
	before,
	label,
	after,
}: {
	busy: boolean | undefined
	before: ReactNode
	label: string | undefined
	after: ReactNode
}) {
	return (
		<>
			{busy === true ? <span className="tb-button__spinner" aria-hidden="true" /> : before}
			{label}
			{after}
		</>
	)
}

/** The props that decide the look, which never reach the element as attributes. */
const LOOK: readonly (keyof Shared)[] = [
	"variant",
	"size",
	"quiet",
	"icon",
	"busy",
	"label",
	"before",
	"after",
	"className",
]

/** Everything the caller passed that belongs on the element itself. */
function attributesOf<P extends Shared>(props: P): Omit<P, keyof Shared> {
	const rest: Partial<P> = { ...props }
	for (const key of LOOK) delete rest[key]
	return rest as Omit<P, keyof Shared>
}

export function Button(props: ButtonProps) {
	const classes = classesOf(props)
	const ariaBusy = props.busy === true ? true : undefined
	const content = (
		<Content busy={props.busy} before={props.before} label={props.label} after={props.after} />
	)
	if (props.href !== undefined) {
		return (
			<a className={classes} aria-busy={ariaBusy} {...attributesOf(props)}>
				{content}
			</a>
		)
	}
	const { type = "button", ...button } = attributesOf(props)
	return (
		<button type={type} className={classes} aria-busy={ariaBusy} {...button}>
			{content}
		</button>
	)
}
